import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { localeSelector } from '@xarvio/frontend-core-state/lib/intl/selectors';
import { FeatureToggle } from '../components/shared/FeatureToggle/FeatureToggle';
import {
  accountInfoSelector,
  getIsUserLoggedInSelector,
  getUserAccessTokenSelector,
} from '@xarvio/frontend-core-state/lib/auth/selectors';
import { PublicRoutes } from './PublicRoutes/PublicRoutes';
import { PageTracker as ATInternetPageTracker } from './ATInternetTracker/PageTracker/PageTracker';
import { useSetConfiguredTag as useSetConfiguredATInternetTrackingTag } from './ATInternetTracker/useSetConfiguredTag/useSetConfiguredTag';
import { useEffectOnce } from 'react-use';
import { LocalStorageKey, LocalStorageUtil } from '../utils/LocalStorageUtil';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  LocalStorageCookieConsent,
  setDdUser,
  syncAnalyticsState,
} from '../utils/analyticsUtils';
import {
  getReturnUrl,
  appendHandoffToken,
} from '@xarvio/frontend-core-platform/lib/returnUrl';
import { RouteLoadingFallback } from './RouteLoadingFallback';
import { isLoginRoute } from './PublicRoutes/authRouteUtils';

const PrivateRoutes = lazy(() =>
  import('./PrivateRoutes/PrivateRoutes').then((m) => ({
    default: m.PrivateRoutes,
  })),
);
const PendoTracking = lazy(() =>
  import('./PrivateRoutes/PendoTracking/PendoTracking').then((m) => ({
    default: m.PendoTracking,
  })),
);
const StagingLokalizeConnect = lazy(() =>
  import('../components/shared/StagingLokalize/connect').then((m) => ({
    default: m.StagingLokalizeConnect,
  })),
);
const DroneImageryUploadQueueProvider = lazy(() =>
  import(
    './DroneImageryUploadQueueContext/DroneImageryUploadQueueProvider'
  ).then((m) => ({
    default: m.DroneImageryUploadQueueProvider,
  })),
);

export const AppRouter: React.FC<React.PropsWithChildren<unknown>> = () => {
  const locale = useSelector(localeSelector);
  const isUserLoggedIn = useSelector(getIsUserLoggedInSelector);
  const userAccountInfo = useSelector(accountInfoSelector);
  const userAccessToken = useSelector(getUserAccessTokenSelector);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isXarvioHf = searchParams.get('isXarvioHf');

  useEffectOnce(() => {
    if (isXarvioHf && isUserLoggedIn) {
      LocalStorageUtil.set(LocalStorageKey.IS_XARVIO_HF_LOGOUT_REQUIRED, true);
    } else if (isXarvioHf && !isUserLoggedIn) {
      LocalStorageUtil.set(LocalStorageKey.IS_XARVIO_HF, true);
    }
  });

  useEffect(() => {
    if (!isUserLoggedIn) {
      return;
    }

    const returnUrl = getReturnUrl(searchParams);

    if (returnUrl) {
      window.location.href = appendHandoffToken(returnUrl, userAccessToken);
    }
  }, [isUserLoggedIn, searchParams, userAccessToken]);

  const { isAtInternetEnabled, isDatadogEnabled } = useMemo(() => {
    let isAtInternetEnabled = false;
    let isDatadogEnabled = false;

    const analyticsConsent = LocalStorageUtil.get(
      LocalStorageKey.COOKIE_BANNER_CONSENT,
    ) as string | undefined;

    const parsedAnalyticsConsent = analyticsConsent
      ? (JSON.parse(analyticsConsent) as LocalStorageCookieConsent | undefined)
      : undefined;

    const hasJapaneseLocale = locale == 'ja_jp' || locale == 'en_jp';

    isAtInternetEnabled = !!parsedAnalyticsConsent?.isAtInternetEnabled;
    isDatadogEnabled =
      hasJapaneseLocale || !!parsedAnalyticsConsent?.isDatadogEnabled;

    // Temporarily disable datadog on QA for Japan
    if (hasJapaneseLocale) {
      if (import.meta.env.VITE_ENVIRONMENT === 'QA') {
        isDatadogEnabled = false;
      }
    }

    return {
      isAtInternetEnabled,
      isDatadogEnabled,
    };
  }, [locale]);

  useEffect(() => {
    syncAnalyticsState({
      isAtInternetEnabled,
      isDatadogEnabled,
    });
  }, [isAtInternetEnabled, isDatadogEnabled]);

  useEffect(() => {
    if (isUserLoggedIn && userAccountInfo && isDatadogEnabled) {
      setDdUser(userAccountInfo);
    }
  }, [isUserLoggedIn, userAccountInfo, isDatadogEnabled]);

  useSetConfiguredATInternetTrackingTag();
  const isRemote = import.meta.env.VITE_IS_REMOTE_DEPLOYMENT === 'true';

  return (
    <>
      <ATInternetPageTracker />
      {!isUserLoggedIn || isLoginRoute(pathname) ? (
        <PublicRoutes />
      ) : (
        <Suspense fallback={<RouteLoadingFallback />}>
          <DroneImageryUploadQueueProvider>
            <PrivateRoutes />
          </DroneImageryUploadQueueProvider>
        </Suspense>
      )}
      <FeatureToggle featureName="LOKALISE_INPLACE_EDIT">
        <Suspense fallback={null}>
          <StagingLokalizeConnect />
        </Suspense>
      </FeatureToggle>
      <FeatureToggle featureName="PENDO_TRACKING">
        <Suspense fallback={null}>{isRemote && <PendoTracking />}</Suspense>
      </FeatureToggle>
    </>
  );
};
